import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { postApi } from "../../config/configAxios";
import { parseJSON } from "../../utils/utils";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (loginData, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await postApi("/login", loginData);
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Login failed" });
    }
  },
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axiosInstance.get("/me");
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Failed to fetch user" },
      );
    }
  },
);

export const logoutUserApi = createAsyncThunk(
  "auth/logoutUserApi",
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axiosInstance.post("/logout");
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Logout failed" },
      );
    }
  },
);

const initialState = {
  token: localStorage.getItem("token") || null,
  user: parseJSON(localStorage.getItem("user")) || null,
  permissions: parseJSON(localStorage.getItem("permissions"), []) || [],
  isLoggedIn: !!localStorage.getItem("token"),
  status: "idle",
  error: null,
  success: null,
};

const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("permissions");
  localStorage.removeItem("isLoggedIn");
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutUser(state) {
      state.status = "idle";
      state.user = null;
      state.token = null;
      state.permissions = [];
      state.isLoggedIn = false;
      state.error = null;
      state.success = null;

      clearAuthStorage();
    },
    clearMessage(state) {
      state.error = null;
      state.success = null;
    },
    syncAuthFromStorage(state) {
      state.token = localStorage.getItem("token") || null;
      state.user = parseJSON(localStorage.getItem("user")) || null;
      state.permissions =
        parseJSON(localStorage.getItem("permissions"), []) || [];
      state.isLoggedIn = !!localStorage.getItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.success = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const user = action.payload.user;
        const token = action.payload.token;
        const permissions = user?.permissions || [];

        state.status = "succeeded";
        state.user = user;
        state.token = token;
        state.permissions = permissions;
        state.isLoggedIn = true;
        state.error = null;
        state.success = "Login successful";

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("permissions", JSON.stringify(permissions));
        localStorage.setItem("isLoggedIn", "true");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.success = null;
        state.error =
          typeof action.payload?.error === "string"
            ? action.payload.error
            : "Login failed";
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        const user = action.payload;
        const permissions = user?.permissions || [];

        state.status = "succeeded";
        state.user = user;
        state.permissions = permissions;
        state.isLoggedIn = !!state.token;
        state.error = null;

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("permissions", JSON.stringify(permissions));
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload?.error === "string"
            ? action.payload.error
            : "Failed to fetch user";
      })
      .addCase(logoutUserApi.fulfilled, (state) => {
        state.status = "idle";
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.isLoggedIn = false;
        state.error = null;
        state.success = null;

        clearAuthStorage();
      })
      .addCase(logoutUserApi.rejected, (state) => {
        state.status = "idle";
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.isLoggedIn = false;
        state.error = null;
        state.success = null;

        clearAuthStorage();
      });
  },
});

export const { logoutUser, clearMessage, syncAuthFromStorage } =
  authSlice.actions;

export default authSlice.reducer;